import React, { Component } from 'react';
import '../css/components/select.css';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const KEY_CODE_ENTER = 13;
const KEY_CODE_UP_ARROW = 38;
const KEY_CODE_DOWN_ARROW = 40;

// Options passed to Select vary across callers: some are instances of Option
// subclasses (with getDisplayValue()), others are plain literals with a
// displayValue string property directly.
interface SelectOption {
    value?: unknown;
    displayValue?: string;
    getDisplayValue?: () => string;
}

interface SelectChangeEvent {
    target: {
        name?: string;
        type: string;
        value: SelectOption;
    };
}

interface SelectProps {
    options: SelectOption[];
    selectedOption?: SelectOption | string;
    handleChange: (event: SelectChangeEvent) => void;
    name?: string;
    id?: string;
    style?: React.CSSProperties;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    canSearch?: boolean | string;
}

interface SelectState {
    show: boolean;
    items: SelectOption[];
    allItems: SelectOption[];
    firstOption: SelectOption | string;
    canSearch: boolean;
    searchFilter: string;
}

class Select extends Component<SelectProps, SelectState> {
    mounted?: boolean;
    selectedOptionIndex: number | string;

    constructor(props: SelectProps) {
        super(props);

        this.state = {
            show: false,
            items: props.options,
            allItems: props.options,
            firstOption: props.selectedOption ? props.selectedOption : {},
            canSearch: (typeof props.canSearch !== 'undefined' && props.canSearch === "1"),
            searchFilter: ''
        };

        this.selectedOptionIndex = '';


        this.applyFilters = this.applyFilters.bind(this);
        this.searchKeyPress = this.searchKeyPress.bind(this);
    }

    safeSetState = (stateUpdates: Partial<SelectState>) => {
        if (this.mounted) {
            this.setState(stateUpdates as SelectState)
        }
    }

    componentDidMount() {
        this.mounted = true;
    }

    UNSAFE_componentWillReceiveProps(newProps: SelectProps) {
        if (this.props.options !== newProps.options) {
            this.safeSetState({ allItems: newProps.options });
            this.safeSetState({ firstOption: newProps.options[0] });
            this.safeSetState({ items: newProps.options });
        }
    }

    componentWillUnmount() {
        this.mounted = false
        document.body.removeEventListener('click', this.hideOptions);
    }

    showOptions = () => {
        if (this.props.disabled) return;
        this.safeSetState({ show: true });
        document.body.addEventListener('click', this.hideOptions, true);
    };

    hideOptions = () => {
        this.safeSetState({ show: false });
        document.body.removeEventListener('click', this.hideOptions);
    };

    selectOption = (selectedOption: SelectOption, index: number, hideOptions: boolean, scrollIntoView: boolean) => {
        this.selectedOptionIndex = index;
        this.props.handleChange({
            target: {
                name: this.props.name,
                type: 'select',
                value: selectedOption
            }
        });
        if (this.state.canSearch) {
            const searchInput = document.getElementById("searchId") as HTMLInputElement | null;
            if (searchInput) searchInput.value = selectedOption.displayValue || '';
        }
        if (hideOptions) {
            this.hideOptions();
        }
        const selectedListItem = document.getElementById(String(selectedOption.value));
        if (scrollIntoView && selectedListItem) {
            selectedListItem.scrollIntoView(false);
        }
    };

    getSelectClass = () => {
        return this.props.className + " ss-select " + (this.state.canSearch ? "with-search" : "");
    };

    getSelectInputClass = () => {
        return this.props.className ? (this.props.className + " ss-select-text " + (this.props.placeholder ? "" : "ss-select-caret")) : "";
    };

    applyFilters = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.showOptions();
        let updatedList = this.state.allItems;
        // Trim so a stray leading/trailing space (e.g. pasted from elsewhere)
        // doesn't prevent an otherwise-exact match.
        const searchFilter = event.target.value.toLowerCase().trim();
        let firstOption: SelectOption | undefined;

        if (searchFilter.length > 0) {
            let count = 0;
            updatedList = updatedList.filter(function (item) {
                // Plain substring match. This used to call String#search(), which
                // treats the query as a regular expression: email addresses that
                // contain regex-special characters (e.g. "jsmith+ops@example.com")
                // would silently fail to match, or a query like an unbalanced "("
                // could throw and break the filter entirely.
                if ((item.displayValue || '').toLowerCase().includes(searchFilter)) {
                    if (count === 0) {
                        firstOption = item;
                    }
                    count++;
                    return true;
                } else return false;
            });
        }
        this.safeSetState(firstOption
            ? { items: updatedList, searchFilter: searchFilter, firstOption: firstOption }
            : { items: updatedList, searchFilter: searchFilter });
    };

    searchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === KEY_CODE_ENTER || event.which === KEY_CODE_ENTER) {
            if (this.state.items && this.state.items.length > 0) {
                const newSelectedOptionIndex = this.isCurrentSelectedIndexValid() ? Number(this.selectedOptionIndex) : 0;
                this.selectOption(this.state.items[newSelectedOptionIndex], newSelectedOptionIndex, true, false);
            }
        } else if (event.keyCode === KEY_CODE_UP_ARROW || event.which === KEY_CODE_UP_ARROW) {
            if (this.state.items && this.state.items.length > 0) {
                const newSelectedOptionIndex = this.shouldDecrementCurrentIndex() ? Number(this.selectedOptionIndex) - 1 : 0;
                this.selectOption(this.state.items[newSelectedOptionIndex], newSelectedOptionIndex, false, true);
            }
        } else if (event.keyCode === KEY_CODE_DOWN_ARROW || event.which === KEY_CODE_DOWN_ARROW) {
            if (this.state.items && this.state.items.length > 0) {
                const newSelectedOptionIndex = this.shouldIncrementCurrentIndex() ? Number(this.selectedOptionIndex) + 1 : 0;
                this.selectOption(this.state.items[newSelectedOptionIndex], newSelectedOptionIndex, false, true);
            }
        } else {
            this.props.handleChange({
                target: {
                    name: this.props.name,
                    type: 'select',
                    value: { "value": null, "displayValue": "" }
                }
            });
        }
    };

    isCurrentSelectedIndexValid = () => {
        return typeof this.selectedOptionIndex === 'number' && this.selectedOptionIndex >= 0 && this.selectedOptionIndex < this.state.items.length;
    };

    shouldIncrementCurrentIndex = () => {
        return typeof this.selectedOptionIndex === 'number' && this.selectedOptionIndex + 1 > 0 && this.selectedOptionIndex + 1 < this.state.items.length;
    };

    shouldDecrementCurrentIndex = () => {
        return typeof this.selectedOptionIndex === 'number' && this.selectedOptionIndex - 1 > 0 && this.selectedOptionIndex - 1 < this.state.items.length;
    };

    static getDisplayValue(option?: SelectOption | string): string {
        if (option && typeof option !== 'string' && typeof option.getDisplayValue === "function") {
            return option.getDisplayValue();
        }
        return option ? String(option) : '';
    }

    render() {
        let inputElement;

        if (!this.state.canSearch) {
            //old version
            inputElement = <input type="text"
                id={this.props.id}
                name={this.props.name}
                style={this.props.style}
                value={Select.getDisplayValue(this.props.selectedOption)}
                onClick={this.showOptions}
                className={this.getSelectInputClass()}
                placeholder={this.props.placeholder}
                disabled={this.props.disabled}
                readOnly
            />
        } else {
            // interactive search
            inputElement = <input type="text"
                id="searchId"
                name="searchName"
                style={this.props.style}
                onClick={this.showOptions}
                placeholder={this.props.placeholder}
                onChange={this.applyFilters}
                onKeyDown={this.searchKeyPress}
                autoComplete="off"
                disabled={this.props.disabled}
            />
        }

        return (

            <div className={this.getSelectClass()}>
                <div className='ss-dropbox'>
                    {inputElement}
                    <KeyboardArrowDownIcon onClick={this.showOptions} />
                </div>
                <div className={this.state.show ? "ss-select-options-visible" : "ss-select-options-hidden"} >
                    <ul className="ss-select-list">
                        {
                            this.state.items.length === 0 && this.state.canSearch && this.state.searchFilter.length > 0
                                ? <li className="ss-select-item ss-select-no-results">No matches found</li>
                                : this.state.items.map((option, index) =>
                                    <li id={option ? String(option.value) : String(index)}
                                        key={index}
                                        className={option === this.props.selectedOption ? "ss-select-item sel-item" : "ss-select-item"}
                                        onClick={() => this.selectOption(option, index, true, false)}>
                                        {Select.getDisplayValue(option)}
                                    </li>
                                )
                        }
                    </ul>
                </div>
            </div>
        );
    }
}

export default Select;
